import { useId, useState } from "react";
import {
  type AntibiogramEntry,
  antibiogramFromPanel,
  antibioticAnswers,
  type AntibioticPanels,
  cultureSummary,
  defaultGram,
  EMPTY_ANTIBIOTIC_PANELS,
  type Gram,
  GRAM_LABEL,
  nameKey,
  type OrderItemResults,
  orderAntibiogram,
  PANEL_LABEL,
  type PanelGram,
  richTextToPlain,
  sanitizeRichText,
  SUSCEPTIBILITY_ANSWERS,
  type SusceptibilityAnswer,
} from "@probetalab/contracts";
import { type CultureDraft, cultureFromDraft, cultureKey, cultureOf, cultureResult, cultureToDraft, type Draft, hasPanelGram, type IsolateDraft, NOTE_KEY, pickKey } from "./CaptureRows.js";
import { NO_AUTOFILL } from "../lib/noAutofill.js";
import { Icon } from "./icons.js";
import { DocumentSheet, RichTextEditor, RichTextView } from "./RichText.js";
import { Button, Chip, IconButton, Sheet } from "./ui.js";

/**
 * Capture of formatted documents, cultures and study notes (ADR 0019).
 *
 * A culture (a study with a document row and antibiotic rows) is captured as the lab thinks of
 * it, not as rows: first the outcome (Negativo / Positivo), then the organism(s) and count, the
 * report text, and — only when positive — the antibiogram, where the chemist adds the
 * antibiotics that apply to this organism and answers each with the study's own answer list.
 * Chosen antibiotics keep the study's order; the ones not chosen do not exist for this order.
 *
 * ADR 0026: each organism gets a Gram (Gram negativo / Gram positivo / No aplica). A Gram with a
 * panel preloads the laboratory's list for it (Catálogo → Antibióticos) as that organism's own
 * antibiogram, still adjustable with «Agregar o quitar»; an organism the laboratory classified
 * before preselects its Gram. An unclassified culture works exactly as above.
 */

type Analyte = OrderItemResults["analytes"][number];
type OnChange = (key: string, value: string) => void;

/** Unsaved when the draft differs from what the server holds for this key. */
function unsaved(saved: string | undefined, now: string | undefined): boolean {
  return (saved ?? "").trim() !== (now ?? "").trim();
}

/** The report text of a document row: a preview that opens the editor. */
export function DocumentField({ label, value, saved, locked, hint, onChange }: { label: string; value: string; saved: string; locked: boolean; hint?: string; onChange: (html: string) => void }) {
  const [open, setOpen] = useState(false);
  const empty = richTextToPlain(value) === "";
  const dirty = unsaved(sanitizeRichText(saved), value);
  return (
    <>
      <button type="button" className={`doc-preview${empty ? " empty" : ""}`} onClick={() => setOpen(true)} disabled={locked && empty} aria-label={`${label}: ${empty ? "escribir" : locked ? "ver" : "editar"} informe`}>
        {empty ? (
          <span className="doc-preview-empty">
            <Icon name="pen" /> {locked ? "Sin informe" : (hint ?? "Toca para escribir el informe")}
          </span>
        ) : (
          <RichTextView html={value} clamp={4} />
        )}
        {!empty && (
          <span className="doc-preview-foot">
            {dirty ? (
              <Chip tone="amber" dot>
                Sin guardar
              </Chip>
            ) : (
              <span />
            )}
            <span className="doc-preview-action">
              <Icon name={locked ? "eye" : "pen"} /> {locked ? "Ver" : "Editar"}
            </span>
          </span>
        )}
      </button>
      <DocumentSheet open={open} title={label} value={value} readOnly={locked} onApply={onChange} onClose={() => setOpen(false)} subtitle={locked ? undefined : "Se imprime en el reporte tal como lo ves: negritas, cursiva, alineación y listas."} />
    </>
  );
}

/** A document analyte that is not a culture (cytology, a pathology note): one full-width row. */
export function DocumentRow({ a, r, draft, locked, onChange }: { a: Analyte; r: OrderItemResults; draft: Draft; locked: boolean; onChange: OnChange }) {
  const saved = r.values.find((v) => v.analyte_id === a.analyte_id)?.value_text ?? "";
  return (
    <div className="analyte-row doc-row">
      <div>
        <div className="aname">{a.name}</div>
        <div className="acode">{a.code} · Documento</div>
      </div>
      <DocumentField label={a.name} value={draft[a.analyte_id] ?? ""} saved={saved} locked={locked} onChange={(html) => onChange(a.analyte_id, html)} />
    </div>
  );
}

const MAX_ISOLATES = 3;

/** A count that starts with "más de" (or ">", "≥", "mayor de/a"): printed "de más de … de:" (report v9). */
export const MORE_THAN = /^\s*(?:>=?|≥|mayor\s+(?:de|a|que)|m[aá]s\s+de)\s*/i;
export const isMoreThan = (count: string | null | undefined) => MORE_THAN.test(count ?? "");
export const withoutMoreThan = (count: string | null | undefined) => (count ?? "").replace(MORE_THAN, "");
/** The count with «Más de» switched; the number the chemist typed is kept as typed. */
export const toggleMoreThan = (count: string | null | undefined) => (isMoreThan(count) ? withoutMoreThan(count) : `más de ${withoutMoreThan(count)}`);

const escapeRegExp = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

/**
 * The count without its unit (pilot R-57): ONE trailing occurrence of the catalogue unit goes, any case,
 * with the single space that separated it (and any spaces after it). It must follow a digit, a space or
 * the start, so «mL» never eats the end of «UFC/mL». Anything else the laboratory typed is returned as is.
 */
export function stripCountUnit(count: string | null | undefined, unit: string | null | undefined): string {
  const text = count ?? "";
  const u = (unit ?? "").trim();
  if (!u) return text;
  return text.replace(new RegExp(`(?<=^|[\\s\\d])\\s?${escapeRegExp(u)}\\s*$`, "i"), "");
}

/** The number the chemist typed with the unit after it: «100 000» → «100 000 UFC/mL»; nothing typed stays nothing. */
export function withCountUnit(number: string, unit: string | null | undefined): string {
  const u = (unit ?? "").trim();
  if (!u) return number;
  if (number.trim() === "") return "";
  return `${stripCountUnit(number, u)} ${u}`;
}

/** What the number box shows for a stored count: no «Más de», no unit. */
export const countNumber = (count: string | null | undefined, unit: string | null | undefined) => stripCountUnit(withoutMoreThan(count), unit);

/** The stored count after the chemist edits the number box (keeps «Más de»; adds the unit; empty stays empty). */
export function countAfterTyping(previous: string | null | undefined, typed: string, unit: string | null | undefined): string {
  return (isMoreThan(previous) || MORE_THAN.test(typed) ? "más de " : "") + withCountUnit(withoutMoreThan(typed), unit);
}

const GRAM_OPTIONS: { value: Gram; label: string }[] = [
  { value: "negative", label: GRAM_LABEL.negative },
  { value: "positive", label: GRAM_LABEL.positive },
  { value: "none", label: GRAM_LABEL.none },
];

/** The answer chip's tone class: resistant, intermediate, sensitive. */
const answerTone = (o: string) => (/RESIST/i.test(o) ? " r" : /INTERMED/i.test(o) ? " i" : " s");

/**
 * The isolate with a new organism name. ADR 0026: an organism the laboratory (or the common
 * list) already classified preselects its Gram and preloads that panel, unless the chemist chose
 * the Gram by hand or has answered something — then only the name changes.
 */
export function withOrganism(iso: IsolateDraft, organism: string, panels: AntibioticPanels, labGrams: Record<string, Gram>): IsolateDraft {
  const next = { ...iso, organism };
  const suggested = suggestedGram(organism, panels, labGrams);
  if (!suggested) return next;
  const answered = (iso.antibiogram ?? []).some((e) => e.result !== null);
  const auto = iso.gram === undefined || (!answered && iso.gram === suggestedGram(iso.organism, panels, labGrams));
  return auto && suggested !== iso.gram ? withGram(next, suggested, panels) : next;
}

/**
 * The Gram to preselect: what the laboratory recorded for the organism last time; the common
 * organisms' Gram only once the laboratory has filled at least one panel, so a laboratory that
 * has not set up its lists keeps working exactly as before ADR 0026.
 */
export function suggestedGram(organism: string, panels: AntibioticPanels, labGrams: Record<string, Gram>): Gram | null {
  const configured = panels.positive.length > 0 || panels.negative.length > 0;
  return configured ? defaultGram(organism, labGrams) : (labGrams[nameKey(organism)] ?? null);
}

/** The isolate classified: a Gram with a panel preloads it (keeping answers given); "No aplica" has no antibiogram. */
export function withGram(iso: IsolateDraft, gram: Gram, panels: AntibioticPanels): IsolateDraft {
  if (gram === "none") return { organism: iso.organism, count: iso.count, gram };
  return { ...iso, gram, antibiogram: antibiogramFromPanel(panels[gram], iso.antibiogram ?? []) };
}

/** What «Agregar o quitar» offers an isolate, in print-order groups: its panel, the other one, the study's own antibiotics, anything already chosen. */
export function antibioticChoices(gram: PanelGram, panels: AntibioticPanels, studyAntibiotics: string[], chosen: AntibiogramEntry[]): { title: string; names: string[] }[] {
  const other: PanelGram = gram === "positive" ? "negative" : "positive";
  const seen = new Set<string>();
  const take = (names: readonly string[]) =>
    names.filter((n) => {
      const k = nameKey(n);
      if (!k || seen.has(k)) return false;
      seen.add(k);
      return true;
    });
  return [
    { title: `${PANEL_LABEL[gram]} · lista del laboratorio`, names: take(panels[gram]) },
    { title: PANEL_LABEL[other], names: take(panels[other]) },
    { title: "Del estudio", names: take(studyAntibiotics) },
    { title: "Otros", names: take(chosen.map((e) => e.antibiotic)) },
  ].filter((g) => g.names.length > 0);
}

export function CultureCapture({ r, draft, locked, onChange }: { r: OrderItemResults; draft: Draft; locked: boolean; onChange: OnChange }) {
  const culture = cultureOf(r)!;
  const doc = culture.document;
  /** The unit the laboratory's catalogue gives the count (pilot R-57); none = a free-text count as ever. */
  const countUnit = doc.unit?.trim() || "";
  const key = cultureKey(doc.analyte_id);
  const savedRow = r.values.find((v) => v.analyte_id === doc.analyte_id);
  const c = cultureFromDraft(draft[key]);
  const listId = useId();
  const [picking, setPicking] = useState(false);
  /** The isolate whose antibiogram «Agregar o quitar» is editing. */
  const [pickingIsolate, setPickingIsolate] = useState<number | null>(null);
  const panels = r.antibiotic_panels ?? EMPTY_ANTIBIOTIC_PANELS;
  const labGrams = r.organism_grams ?? {};

  const setCulture = (next: CultureDraft | null) => onChange(key, cultureToDraft(next));
  const setOutcome = (outcome: CultureDraft["outcome"]) => {
    if (c?.outcome === outcome) return;
    setCulture(outcome === "negative" ? { outcome, isolates: [] } : { outcome, isolates: c?.isolates.length ? c.isolates : [{ organism: "", count: "" }] });
  };
  const setIsolate = (i: number, next: IsolateDraft) => {
    if (!c) return;
    setCulture({ ...c, isolates: c.isolates.map((x, j) => (j === i ? next : x)) });
  };

  const chosen = (a: Analyte) => (draft[a.analyte_id] ?? "").trim() !== "" || draft[pickKey(a.analyte_id)] === "1";
  const shown = culture.antibiotics.filter(chosen);
  const toggle = (a: Analyte, on: boolean) => {
    onChange(pickKey(a.analyte_id), on ? "1" : "");
    if (!on) onChange(a.analyte_id, "");
  };
  const outcomeDirty = unsaved(cultureToDraft(savedRow?.culture), draft[key]);
  const positive = c?.outcome === "positive";
  const complete = cultureResult(c) !== null;
  const classified = positive && c ? c.isolates.some(hasPanelGram) : false;
  // ADR 0019's study antibiogram stays for an unclassified culture, and whenever it already has answers.
  const studyAntibiogram = positive && (!classified || shown.length > 0);
  const studyNames = culture.antibiotics.map((a) => a.name);
  const savedIsolates = savedRow?.culture?.isolates ?? [];

  return (
    <div className="culture">
      <section className="culture-sec" aria-label="Resultado del cultivo">
        <div className="culture-head">
          <h3 className="culture-title">
            <Icon name="microbe" /> Resultado del cultivo
          </h3>
          {outcomeDirty && (
            <Chip tone="amber" dot>
              Sin guardar
            </Chip>
          )}
        </div>
        <div className="outcome" role="radiogroup" aria-label="Resultado del cultivo">
          {(
            [
              ["negative", "Negativo", "Sin desarrollo"],
              ["positive", "Positivo", "Con desarrollo"],
            ] as const
          ).map(([v, label, sub]) => (
            <button
              key={v}
              type="button"
              role="radio"
              aria-checked={c?.outcome === v}
              className={`outcome-opt ${v}${c?.outcome === v ? " on" : ""}`}
              disabled={locked}
              onClick={() => setOutcome(v)}
            >
              <span className="outcome-label">{label}</span>
              <span className="outcome-sub">{sub}</span>
            </button>
          ))}
        </div>
        {!c && !locked && <p className="muted small">Hasta que lo elijas, la solicitud y el ticket solo dicen «{r.study_name}».</p>}

        {positive && c && (
          <div className="isolates">
            {c.isolates.map((iso, i) => {
              const n = c.isolates.length > 1 ? ` ${i + 1}` : "";
              const learned = labGrams[nameKey(iso.organism)];
              return (
                <div className="isolate-block" key={i}>
                  <div className="isolate">
                    <label className="field">
                      <span className="label">Microorganismo{n}</span>
                      <input
                        className="input organism"
                        list={listId}
                        value={iso.organism}
                        disabled={locked}
                        placeholder="Escribe o elige"
                        {...NO_AUTOFILL}
                        aria-invalid={!locked && iso.organism.trim() === "" ? true : undefined}
                        onChange={(e) => setIsolate(i, withOrganism(iso, e.target.value, panels, labGrams))}
                      />
                    </label>
                    <div className="field">
                      <span className="label" id={`${listId}-count-${i}`}>
                        Cuenta
                      </span>
                      <div className="count-field">
                        {/* «Más de» prints only when the chemist says so (pilot report 2026-11-03 09:53). */}
                        <button
                          type="button"
                          className={`count-more${isMoreThan(iso.count) ? " on" : ""}`}
                          aria-pressed={isMoreThan(iso.count)}
                          disabled={locked}
                          onClick={() => setIsolate(i, { ...iso, count: toggleMoreThan(iso.count) })}
                        >
                          Más de
                        </button>
                        <input
                          className="input"
                          aria-labelledby={`${listId}-count-${i}`}
                          value={countNumber(iso.count, countUnit)}
                          disabled={locked}
                          placeholder={countUnit ? "100 000" : "100 000 UFC/mL"}
                          {...NO_AUTOFILL}
                          onChange={(e) => setIsolate(i, { ...iso, count: countAfterTyping(iso.count, e.target.value, countUnit) })}
                        />
                        {/* The catalogue's unit (pilot R-57): shown, never typed; saved after the number. */}
                        {countUnit && (
                          <span className="count-unit">
                            {countUnit}
                          </span>
                        )}
                      </div>
                    </div>
                    {!locked && c.isolates.length > 1 && (
                      <IconButton icon="x" label={`Quitar microorganismo ${i + 1}`} onClick={() => setCulture({ ...c, isolates: c.isolates.filter((_, j) => j !== i) })} />
                    )}
                  </div>
                  <div className="gram-choice">
                    <span className="label" aria-hidden="true">
                      Gram{n}
                    </span>
                    <div className="seg sm" role="radiogroup" aria-label={`Gram${n}`}>
                      {GRAM_OPTIONS.map((g) => (
                        <button key={g.value} type="button" role="radio" aria-checked={iso.gram === g.value} disabled={locked} onClick={() => iso.gram !== g.value && setIsolate(i, withGram(iso, g.value, panels))}>
                          {g.label}
                        </button>
                      ))}
                    </div>
                    {!locked && iso.gram === undefined && <span className="muted small">Elige el Gram para cargar su lista de antibióticos.</span>}
                    {!locked && iso.gram !== undefined && learned === iso.gram && <span className="muted small">Como lo registró el laboratorio la última vez.</span>}
                    {iso.gram === "none" && <span className="muted small">Sin antibiograma por Gram.</span>}
                  </div>
                </div>
              );
            })}
            <datalist id={listId}>
              {r.organism_suggestions.map((o) => (
                <option key={o} value={o} />
              ))}
            </datalist>
            {!locked && c.isolates.length < MAX_ISOLATES && (
              <div>
                <Button size="sm" variant="ghost" icon="plus" onClick={() => setCulture({ ...c, isolates: [...c.isolates, { organism: "", count: "" }] })}>
                  Agregar otro microorganismo
                </Button>
              </div>
            )}
          </div>
        )}
      </section>

      <section className="culture-sec" aria-label="Informe">
        <h3 className="culture-title">Informe</h3>
        <DocumentField
          label={`${doc.name}: informe`}
          value={draft[doc.analyte_id] ?? ""}
          saved={savedRow?.value_text ?? ""}
          locked={locked}
          hint="Toca para escribir sedimento, Gram y otros hallazgos (opcional)"
          onChange={(html) => onChange(doc.analyte_id, html)}
        />
      </section>

      {positive &&
        c &&
        c.isolates.map((iso, i) =>
          hasPanelGram(iso) ? (
            <IsolateAntibiogram
              key={`abx-${i}`}
              iso={iso}
              many={c.isolates.length > 1}
              panel={panels[iso.gram]}
              saved={savedIsolates[i]?.antibiogram ?? []}
              locked={locked}
              onChange={(antibiogram) => setIsolate(i, { ...iso, antibiogram })}
              onPick={() => setPickingIsolate(i)}
            />
          ) : null,
        )}

      {studyAntibiogram && (
        <section className="culture-sec" aria-label="Antibiograma">
          <div className="culture-head">
            <h3 className="culture-title">
              Antibiograma <span className="muted small">· {shown.length} de {culture.antibiotics.length}</span>
            </h3>
            {!locked && (
              <Button size="sm" variant="secondary" icon="plus" onClick={() => setPicking(true)}>
                {shown.length ? "Agregar o quitar" : "Elegir antibióticos"}
              </Button>
            )}
          </div>
          {shown.length === 0 ? (
            <p className="muted small">Elige los antibióticos que aplican a este microorganismo. Solo esos se imprimen, en el orden del estudio.</p>
          ) : (
            <ul className="abx-list">
              {shown.map((a) => {
                const answer = draft[a.analyte_id] ?? "";
                const savedAnswer = r.values.find((v) => v.analyte_id === a.analyte_id)?.value_text ?? "";
                return (
                  <li key={a.analyte_id} className={`abx${answer ? "" : " unanswered"}`}>
                    <div className="abx-name">
                      {a.name}
                      {!answer && <span className="abx-hint">Elige la respuesta</span>}
                      {answer && answer !== savedAnswer && <span className="abx-hint unsaved">Sin guardar</span>}
                    </div>
                    <div className="abx-answers" role="radiogroup" aria-label={a.name}>
                      {antibioticAnswers(a).map((o) => (
                        <button key={o} type="button" role="radio" aria-checked={answer === o} className={`abx-answer${answer === o ? " on" : ""}${answerTone(o)}`} disabled={locked} onClick={() => onChange(a.analyte_id, o)}>
                          {o}
                        </button>
                      ))}
                    </div>
                    {!locked && <IconButton icon="x" label={`Quitar ${a.name}`} onClick={() => toggle(a, false)} />}
                  </li>
                );
              })}
            </ul>
          )}
        </section>
      )}
      {c?.outcome === "negative" && <p className="muted small culture-note">Un cultivo negativo se imprime sin antibiograma.</p>}
      {!locked && c && !complete && <p className="small culture-warn">Falta el microorganismo para que el estudio quede completo.</p>}

      <Sheet
        open={picking}
        onClose={() => setPicking(false)}
        title="Antibióticos de este cultivo"
        footer={
          <Button variant="primary" icon="check" onClick={() => setPicking(false)}>
            Listo
          </Button>
        }
      >
        <p className="muted small">Se imprimen en este orden, el del estudio. Quitar uno aquí no cambia el catálogo.</p>
        <ul className="abx-pick">
          {culture.antibiotics.map((a) => {
            const on = chosen(a);
            return (
              <li key={a.analyte_id}>
                <label className={`abx-pick-row${on ? " on" : ""}`}>
                  <input type="checkbox" checked={on} onChange={(e) => toggle(a, e.target.checked)} />
                  <span>{a.name}</span>
                  {draft[a.analyte_id] && <span className="muted small">{draft[a.analyte_id]}</span>}
                </label>
              </li>
            );
          })}
        </ul>
      </Sheet>

      {pickingIsolate !== null && c?.isolates[pickingIsolate] && hasPanelGram(c.isolates[pickingIsolate]!) && (
        <IsolateAntibioticPicker
          iso={c.isolates[pickingIsolate] as IsolateDraft & { gram: PanelGram }}
          panels={panels}
          studyNames={studyNames}
          onChange={(antibiogram) => setIsolate(pickingIsolate, { ...c.isolates[pickingIsolate]!, antibiogram })}
          onClose={() => setPickingIsolate(null)}
        />
      )}
    </div>
  );
}

/** One classified isolate's antibiogram (ADR 0026): its panel preloaded, answered with the fixed susceptibility answers. */
function IsolateAntibiogram({
  iso,
  many,
  panel,
  saved,
  locked,
  onChange,
  onPick,
}: {
  iso: IsolateDraft & { gram: PanelGram };
  many: boolean;
  panel: string[];
  saved: AntibiogramEntry[];
  locked: boolean;
  onChange: (antibiogram: AntibiogramEntry[]) => void;
  onPick: () => void;
}) {
  const entries = iso.antibiogram ?? [];
  const answered = entries.filter((e) => e.result !== null).length;
  const savedAnswer = new Map(saved.map((e) => [nameKey(e.antibiotic), e.result]));
  const title = many && iso.organism.trim() ? iso.organism.trim() : null;
  const set = (name: string, result: SusceptibilityAnswer) => onChange(entries.map((e) => (e.antibiotic === name ? { ...e, result } : e)));
  return (
    <section className="culture-sec" aria-label={`Antibiograma de ${iso.organism.trim() || "microorganismo"}`}>
      <div className="culture-head">
        <h3 className="culture-title">
          Antibiograma
          {title && <em className="abx-organism">{title}</em>}
          <span className="muted small">
            · {GRAM_LABEL[iso.gram]} · {answered} de {entries.length} con respuesta
          </span>
        </h3>
        {!locked && (
          <Button size="sm" variant="secondary" icon="plus" onClick={onPick}>
            {entries.length ? "Agregar o quitar" : "Elegir antibióticos"}
          </Button>
        )}
      </div>
      {panel.length === 0 && entries.length === 0 && (
        <p className="muted small">La lista de {PANEL_LABEL[iso.gram]} del laboratorio está vacía. Elige aquí los antibióticos de este cultivo; el administrador la arma en Catálogo → Antibióticos.</p>
      )}
      {entries.length > 0 && (
        <>
          <ul className="abx-list">
            {entries.map((e) => {
              const was = savedAnswer.get(nameKey(e.antibiotic));
              return (
                <li key={e.antibiotic} className={`abx${e.result ? "" : " unanswered"}`}>
                  <div className="abx-name">
                    {e.antibiotic}
                    {!e.result && <span className="abx-hint">Sin respuesta: no se imprime</span>}
                    {e.result && e.result !== was && <span className="abx-hint unsaved">Sin guardar</span>}
                  </div>
                  <div className="abx-answers" role="radiogroup" aria-label={e.antibiotic}>
                    {SUSCEPTIBILITY_ANSWERS.map((o) => (
                      <button key={o} type="button" role="radio" aria-checked={e.result === o} className={`abx-answer${e.result === o ? " on" : ""}${answerTone(o)}`} disabled={locked} onClick={() => set(e.antibiotic, o)}>
                        {o}
                      </button>
                    ))}
                  </div>
                  {!locked && <IconButton icon="x" label={`Quitar ${e.antibiotic}`} onClick={() => onChange(entries.filter((x) => x.antibiotic !== e.antibiotic))} />}
                </li>
              );
            })}
          </ul>
          <p className="muted small">Se imprimen solo los que tienen respuesta, en este orden.</p>
        </>
      )}
    </section>
  );
}

/** «Agregar o quitar» for one isolate: the lab's panels and the study's antibiotics, plus a name typed by hand. */
function IsolateAntibioticPicker({ iso, panels, studyNames, onChange, onClose }: { iso: IsolateDraft & { gram: PanelGram }; panels: AntibioticPanels; studyNames: string[]; onChange: (antibiogram: AntibiogramEntry[]) => void; onClose: () => void }) {
  const entries = iso.antibiogram ?? [];
  const [other, setOther] = useState("");
  const groups = antibioticChoices(iso.gram, panels, studyNames, entries);
  const reference = groups.flatMap((g) => g.names);
  const byKey = new Map(entries.map((e) => [nameKey(e.antibiotic), e]));
  const toggle = (name: string, on: boolean) => {
    if (!on) return onChange(entries.filter((e) => nameKey(e.antibiotic) !== nameKey(name)));
    if (byKey.has(nameKey(name))) return;
    onChange(orderAntibiogram([...entries, { antibiotic: name, result: null }], reference));
  };
  const typed = other.trim();
  const addTyped = () => {
    if (!typed) return;
    toggle(typed, true);
    setOther("");
  };
  return (
    <Sheet
      open
      onClose={onClose}
      title={`Antibióticos · ${iso.organism.trim() || "microorganismo"}`}
      footer={
        <Button variant="primary" icon="check" onClick={onClose}>
          Listo
        </Button>
      }
    >
      <p className="muted small">Se imprimen en el orden de la lista del laboratorio. Quitar uno aquí no cambia el catálogo.</p>
      {groups.map((g) => (
        <div key={g.title} className="abx-pick-group">
          <h4 className="abx-pick-title">{g.title}</h4>
          <ul className="abx-pick">
            {g.names.map((name) => {
              const e = byKey.get(nameKey(name));
              return (
                <li key={name}>
                  <label className={`abx-pick-row${e ? " on" : ""}`}>
                    <input type="checkbox" checked={Boolean(e)} onChange={(ev) => toggle(name, ev.target.checked)} />
                    <span>{name}</span>
                    {e?.result && <span className="muted small">{e.result}</span>}
                  </label>
                </li>
              );
            })}
          </ul>
        </div>
      ))}
      <div className="row" style={{ gap: 8, marginTop: 12 }}>
        <input className="input grow" aria-label="Otro antibiótico" placeholder="Otro antibiótico" value={other} maxLength={120} {...NO_AUTOFILL} onChange={(e) => setOther(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addTyped()} />
        <Button variant="secondary" icon="plus" disabled={!typed || byKey.has(nameKey(typed))} onClick={addTyped}>
          Agregar
        </Button>
      </div>
    </Sheet>
  );
}

/** One line for a culture's outcome where values are listed (Validación, historial). */
export function cultureLine(v: { culture: Parameters<typeof cultureSummary>[0] }): string {
  return cultureSummary(v.culture);
}

/** The note under a study: a compact editor, printed under the study's rows when it has text. */
export function StudyNote({ r, draft, locked, onChange }: { r: OrderItemResults; draft: Draft; locked: boolean; onChange: OnChange }) {
  const value = draft[NOTE_KEY] ?? "";
  const [opened, setOpened] = useState(false);
  const empty = richTextToPlain(value) === "";
  if (locked && empty) return null;
  const dirty = unsaved(sanitizeRichText(r.note ?? ""), value);
  // Most studies carry no note: one quiet button until someone wants to write one.
  if (empty && !opened && !dirty) {
    return (
      <div className="study-note collapsed">
        <Button size="sm" variant="ghost" icon="plus" onClick={() => setOpened(true)}>
          Agregar nota
        </Button>
      </div>
    );
  }
  return (
    <div className="study-note">
      <div className="study-note-head">
        <span className="label">Nota</span>
        <span className="muted small">{locked ? "Se imprime debajo del estudio." : "Opcional. Si la escribes, se imprime debajo del estudio."}</span>
        {dirty && (
          <Chip tone="amber" dot>
            Sin guardar
          </Chip>
        )}
      </div>
      <RichTextEditor value={value} onChange={(html) => onChange(NOTE_KEY, html)} label={`Nota de ${r.study_name}`} compact readOnly={locked} autoFocus={opened} minHeight={56} placeholder="Escribe una nota para el reporte" />
    </div>
  );
}
